/**
 * Gestion de l'expiration de session côté client.
 *
 * Le JWT expire côté serveur (24 h) mais le front maintient `user.loggedIn`
 * persisté (redux-persist) : sans traitement, l'utilisateur reste « dans
 * l'univers connecté » après expiration sans être réellement authentifié.
 * Ces helpers permettent de :
 *  - détecter un token expiré (décodage du JWT, champ exp) ;
 *  - réagir à un 401 renvoyé par le backend pour une requête authentifiée ;
 *  - purger la session (tokens + état persisté) et rediriger vers /login.
 */

const AUTH_KEYS = ["token", "access_token"];

export const getAuthToken = () => {
  try {
    if (typeof window === "undefined") return null;
    return (
      window.localStorage.getItem("token") ||
      window.localStorage.getItem("access_token")
    );
  } catch (e) {
    return null;
  }
};

/** Décode la partie payload d'un JWT (sans vérifier la signature). */
export const decodeJwt = (token) => {
  if (!token || typeof token !== "string") return null;
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      Array.prototype.map
        .call(atob(base64), (c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
};

/** Vrai si le token est expiré ou va expirer dans les 30 secondes. */
export const isTokenExpired = (token) => {
  const payload = decodeJwt(token);
  if (!payload || typeof payload.exp !== "number") return false;
  return Date.now() / 1000 >= payload.exp - 30;
};

/** Purge tokens + état persisté (redux-persist) + flags guest/mock. */
export const clearSession = () => {
  try {
    if (typeof window === "undefined") return;
    AUTH_KEYS.forEach((key) => window.localStorage.removeItem(key));
    window.localStorage.removeItem("persist:admin-app");
    window.localStorage.removeItem("guestMode");
    window.localStorage.removeItem("debugMockUser");
  } catch (e) {
    /* ignore */
  }
};

let redirecting = false;

/**
 * Force la déconnexion : purge la session puis redirige vers /login.
 * S'ajoute un paramètre `?expired=1` pour afficher une mention « session expirée ».
 */
export const triggerSessionExpired = () => {
  if (redirecting || typeof window === "undefined") return;
  redirecting = true;
  clearSession();
  const onLogin = String(window.location.pathname).startsWith("/login");
  if (onLogin) {
    window.location.reload();
  } else {
    window.location.replace("/login?expired=1");
  }
};